/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Download, Send, Trash2, FileSpreadsheet, FileDown } from 'lucide-react';
import invoiceService from '../services/invoiceService';
import './InvoiceDetails.css';

const InvoiceDetails = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInvoiceDetails();
  }, [invoiceId]);

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await invoiceService.getInvoiceById(invoiceId);
      if (response.success) {
        setInvoice(response.invoice);
      }
    } catch (err) {
      console.error('Errore caricamento dettagli:', err);
      setError('Errore nel caricamento dei dettagli della fattura: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await invoiceService.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore download PDF:', err);
      alert('Errore download PDF: ' + err.message);
    }
  };

  const handleSendToAcube = async () => {
    if (!window.confirm(`Inviare fattura ${invoice.invoiceNumber} ad Acube?`)) {
      return;
    }

    try {
      const response = await invoiceService.sendToAcube(invoice.id);
      if (response.success) {
        alert(`Fattura inviata con successo! ID Acube: ${response.acubeInvoiceId}`);
        loadInvoiceDetails(); // Reload to get updated status
      }
    } catch (err) {
      alert('Errore invio ad Acube: ' + err.message);
    }
  };

  const handleDeleteInvoice = async () => {
    const warningMsg = invoice.acubeInvoiceId
      ? `⚠️ ATTENZIONE!\n\nSei sicuro di voler eliminare la fattura ${invoice.invoiceNumber}?\n\n` +
        `Questa fattura è stata INVIATA AD ACUBE (ID: ${invoice.acubeInvoiceId}).\n` +
        `Verrà eliminata anche da Acube.\n\n` +
        `Questo rilascerà ${invoice.transactionCount} transazioni che torneranno disponibili.\n\n` +
        `Questa azione NON è reversibile!`
      : `⚠️ ATTENZIONE!\n\nSei sicuro di voler eliminare la fattura ${invoice.invoiceNumber}?\n\n` +
        `Questo rilascerà ${invoice.transactionCount} transazioni che torneranno disponibili per essere fatturate nuovamente.\n\n` +
        `Questa azione NON è reversibile!`;

    if (!window.confirm(warningMsg)) {
      return;
    }

    try {
      const response = await invoiceService.deleteInvoice(invoice.id);
      if (response.success) {
        alert(`✅ Fattura eliminata con successo!\n${response.transfersReleased} transazioni rilasciate.`);
        navigate('/admin/invoices');
      }
    } catch (err) {
      alert('❌ Errore eliminazione fattura: ' + err.message);
    }
  };

  const handleDownloadCSV = () => {
    if (!invoice || !invoice.tipTransfers) return;

    // Prepare CSV content
    const headers = ['Data', 'Venue', 'Device', 'Lordo (€)', 'Fee Stripe (€)', 'Fee SoonTip (€)', 'Fee Totale (€)', 'Netto (€)', 'Status'];
    const rows = invoice.tipTransfers.map(transfer => {
      const venueName = transfer.tipPaymentIntent?.venue?.name || 'N/A';
      const deviceLabel = transfer.tipPaymentIntent?.device?.label || transfer.tipPaymentIntent?.device?.setupCode || 'N/A';
      const status = transfer.hasReversal ? 'Reversato' : 'OK';

      return [
        new Date(transfer.createdAt).toLocaleDateString('it-IT'),
        venueName,
        deviceLabel,
        (transfer.amount / 100).toFixed(2),
        (transfer.feeStripe / 100).toFixed(2),
        (transfer.feeSoontip / 100).toFixed(2),
        (transfer.feeTotal / 100).toFixed(2),
        ((transfer.amount - transfer.feeTotal) / 100).toFixed(2),
        status
      ];
    });

    // Add totals row
    rows.push([
      'TOTALI',
      '',
      '',
      (invoice.totalGross / 100).toFixed(2),
      (invoice.totalFeeStripe / 100).toFixed(2),
      (invoice.totalFeeSoontip / 100).toFixed(2),
      (invoice.totalFeeTotal / 100).toFixed(2),
      (invoice.totalNet / 100).toFixed(2),
      ''
    ]);

    // Create CSV string
    const csvContent = [
      `Fattura: ${invoice.invoiceNumber}`,
      `Percettore: ${invoice.workerPaymentProfile?.user?.email || 'N/A'}`,
      `Periodo: ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}`,
      '',
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    // Download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fattura-${invoice.invoiceNumber}-dettaglio.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = () => {
    if (!invoice || !invoice.tipTransfers) return;

    const user = invoice.workerPaymentProfile?.user;
    const profile = invoice.workerPaymentProfile;

    // Prepare data for Excel
    const data = [
      ['Fattura:', invoice.invoiceNumber],
      [''],
      ['DATI PERCETTORE'],
      ['Nome:', profile?.verified_first_name || 'N/A'],
      ['Cognome:', profile?.verified_last_name || 'N/A'],
      ['Email:', user?.email || 'N/A'],
      ['Codice Fiscale:', profile?.tax_id || 'N/A'],
      [''],
      ['Periodo:', `${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}`],
      [''],
      ['Data', 'Venue', 'Device', 'Lordo (€)', 'Fee Stripe (€)', 'Fee SoonTip (€)', 'Fee Totale (€)', 'Netto (€)', 'Status'],
      ...invoice.tipTransfers.map(transfer => {
        const venueName = transfer.tipPaymentIntent?.venue?.name || 'N/A';
        const deviceLabel = transfer.tipPaymentIntent?.device?.label || transfer.tipPaymentIntent?.device?.setupCode || 'N/A';
        const status = transfer.hasReversal ? 'Reversato' : 'OK';

        return [
          new Date(transfer.createdAt).toLocaleDateString('it-IT'),
          venueName,
          deviceLabel,
          transfer.amount / 100,
          transfer.feeStripe / 100,
          transfer.feeSoontip / 100,
          transfer.feeTotal / 100,
          (transfer.amount - transfer.feeTotal) / 100,
          status
        ];
      }),
      ['TOTALI', '', '', invoice.totalGross / 100, invoice.totalFeeStripe / 100, invoice.totalFeeSoontip / 100, invoice.totalFeeTotal / 100, invoice.totalNet / 100, '']
    ];

    // Create worksheet
    import('xlsx').then(XLSX => {
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dettaglio Fattura');
      XLSX.writeFile(wb, `fattura-${invoice.invoiceNumber}-dettaglio.xlsx`);
    }).catch(err => {
      console.error('Errore caricamento libreria Excel:', err);
      alert('Libreria Excel non disponibile. Usa il download CSV invece.');
    });
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (invoice) => {
    if (invoice.acubeInvoiceStatus === 'sent') {
      return <span className="badge badge-success">Inviata ad Acube</span>;
    } else if (invoice.acubeInvoiceStatus === 'error') {
      return <span className="badge badge-error">Errore Acube</span>;
    } else {
      return <span className="badge badge-pending">In Attesa</span>;
    }
  };

  if (loading) {
    return (
      <div className="invoice-details-page">
        <div className="loading">Caricamento dettagli fattura...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-details-page">
        <div className="alert alert-error">{error}</div>
        <button onClick={() => navigate('/admin/invoices')} className="btn btn-secondary">
          <ArrowLeft size={18} />
          Torna alla Lista
        </button>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-details-page">
        <div className="alert alert-error">Fattura non trovata</div>
        <button onClick={() => navigate('/admin/invoices')} className="btn btn-secondary">
          <ArrowLeft size={18} />
          Torna alla Lista
        </button>
      </div>
    );
  }

  return (
    <div className="invoice-details-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <button onClick={() => navigate('/admin/invoices')} className="btn-back">
            <ArrowLeft size={20} />
            Torna alla Lista
          </button>
          <h1>
            <FileText size={32} />
            Dettagli Fattura {invoice.invoiceNumber}
          </h1>
        </div>
        <div className="header-actions">
          {invoice.acubeInvoiceId ? (
            <button onClick={handleDownloadPdf} className="btn btn-primary">
              <Download size={18} />
              PDF Fattura
            </button>
          ) : (
            <button onClick={handleSendToAcube} className="btn btn-primary">
              <Send size={18} />
              Invia ad Acube
            </button>
          )}
          <button onClick={handleDeleteInvoice} className="btn btn-danger">
            <Trash2 size={18} />
            Elimina
          </button>
        </div>
      </div>

      {/* Invoice Summary */}
      <div className="invoice-summary-card">
        <h2>Riepilogo Fattura</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Percettore:</span>
            <span className="value">{invoice.workerPaymentProfile?.user?.email || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="label">Periodo:</span>
            <span className="value">
              {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
            </span>
          </div>
          <div className="summary-item">
            <span className="label">Transazioni:</span>
            <span className="value">{invoice.transactionCount}</span>
          </div>
          <div className="summary-item">
            <span className="label">Stato:</span>
            <span className="value">{getStatusBadge(invoice)}</span>
          </div>
          {invoice.generatedAt && (
            <div className="summary-item">
              <span className="label">Generata il:</span>
              <span className="value">{formatDate(invoice.generatedAt)}</span>
            </div>
          )}
          {invoice.acubeInvoiceId && (
            <div className="summary-item">
              <span className="label">ID Acube:</span>
              <span className="value">{invoice.acubeInvoiceId}</span>
            </div>
          )}
        </div>
      </div>

      {/* Totals Summary */}
      <div className="totals-card">
        <h2>Totali</h2>
        <div className="totals-grid">
          <div className="total-item">
            <span className="label">💰 Lordo Mance Totale:</span>
            <span className="value amount">{formatCurrency(invoice.totalGross)}</span>
          </div>
          <div className="total-item">
            <span className="label">💳 Commissioni Stripe:</span>
            <span className="value fee">{formatCurrency(invoice.totalFeeStripe)}</span>
          </div>
          <div className="total-item">
            <span className="label">📋 Commissioni SoonTip:</span>
            <span className="value fee">{formatCurrency(invoice.totalFeeSoontip)}</span>
          </div>
          <div className="total-item highlight">
            <span className="label">💼 Totale Commissioni Fatturate:</span>
            <span className="value fee-total">{formatCurrency(invoice.totalFeeTotal)}</span>
          </div>
          <div className="total-item">
            <span className="label">👤 Netto al Percettore:</span>
            <span className="value net">{formatCurrency(invoice.totalNet)}</span>
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="transfers-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Transfer Inclusi nella Fattura</h2>
          <button onClick={handleDownloadExcel} className="btn btn-secondary">
            <FileSpreadsheet size={18} />
            Scarica Report dettagliato (.xls)
          </button>
        </div>
        {invoice.tipTransfers && invoice.tipTransfers.length > 0 ? (
          <div className="table-wrapper">
            <table className="transfers-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Venue</th>
                  <th>Device</th>
                  <th>Lordo</th>
                  <th>Fee Stripe</th>
                  <th>Fee SoonTip</th>
                  <th>Fee Totale</th>
                  <th>Netto</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoice.tipTransfers.map((transfer) => {
                  const gross = transfer.amount;
                  const venueName = transfer.tipPaymentIntent?.venue?.name || 'N/A';
                  const deviceLabel = transfer.tipPaymentIntent?.device?.label ||
                                    transfer.tipPaymentIntent?.device?.setupCode || 'N/A';

                  return (
                    <tr key={transfer.id} className={transfer.hasReversal ? 'reversed-row' : ''}>
                      <td>{formatDate(transfer.createdAt)}</td>
                      <td>{venueName}</td>
                      <td>{deviceLabel}</td>
                      <td className="amount-cell">{formatCurrency(gross)}</td>
                      <td className="fee-cell">{formatCurrency(transfer.feeStripe)}</td>
                      <td className="fee-cell">{formatCurrency(transfer.feeSoontip)}</td>
                      <td className="fee-cell total-fee">{formatCurrency(transfer.feeTotal)}</td>
                      <td className="net-cell">{formatCurrency(transfer.amount - transfer.feeTotal)}</td>
                      <td>
                        {transfer.hasReversal && (
                          <span className="badge badge-warning" title={`Reversato: ${formatCurrency(transfer.amountReversed || 0)}`}>
                            🔄 Reversato
                          </span>
                        )}
                        {!transfer.hasReversal && (
                          <span className="badge badge-success">✓ OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="3" className="label">TOTALI:</td>
                  <td className="amount-cell">{formatCurrency(invoice.totalGross)}</td>
                  <td className="fee-cell">{formatCurrency(invoice.totalFeeStripe)}</td>
                  <td className="fee-cell">{formatCurrency(invoice.totalFeeSoontip)}</td>
                  <td className="fee-cell total-fee">{formatCurrency(invoice.totalFeeTotal)}</td>
                  <td className="net-cell">{formatCurrency(invoice.totalNet)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="no-data">Nessun transfer trovato</p>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetails;
